import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, LogIn, Mail } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { useLoginOtp } from '@/hooks/useLoginOtp';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;
type LoginMode = 'password' | 'otp';

const OrDivider = () => (
  <div className="flex items-center gap-3 py-1">
    <span className="h-px flex-1 bg-border" />
    <span className="shrink-0 text-xs uppercase text-muted-foreground">or</span>
    <span className="h-px flex-1 bg-border" />
  </div>
);

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isAdmin } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<LoginMode>('password');
  const [otpValue, setOtpValue] = useState('');
  const otpInputRef = useRef<HTMLInputElement | null>(null);

  const {
    step,
    userId: otpUserId,
    sending,
    verifying,
    error: otpError,
    resendSecondsLeft,
    canResend,
    sendOtp,
    resendOtp,
    verifyOtp,
    reset: resetOtp,
  } = useLoginOtp();

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname;

  useEffect(() => {
    if (isAuthenticated) {
      const redirectTo = from || (isAdmin() ? '/admin' : '/dashboard');
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, isAdmin, navigate, from]);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const codeSent = step === 'otp';
  const otpComplete = otpValue.length === 6;

  useEffect(() => {
    if (mode !== 'otp' || !codeSent) return;
    requestAnimationFrame(() => {
      otpInputRef.current?.focus();
    });
  }, [mode, codeSent]);

  const onPasswordSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      await login(data);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
        toast.error(err.message);
      } else {
        setError('Invalid email or password. Please try again.');
        toast.error('Invalid email or password');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignInWithOtp = () => {
    setOtpValue('');
    setError(null);
    resetOtp();
    setMode('otp');
    // Clear password-only validation errors so the shared email field stays editable
    form.clearErrors();
  };

  const handleSendOtp = async () => {
    setError(null);
    const isEmailValid = await form.trigger('email');
    if (!isEmailValid) return;

    const email = form.getValues('email').trim();
    await sendOtp(email);
  };

  const handleOtpSignIn = async () => {
    if (!otpComplete || verifying || sending || !codeSent) return;
    await verifyOtp(otpValue);
  };

  const handleOtpChange = (value: string) => {
    setOtpValue(value.replace(/\D/g, '').slice(0, 6));
  };

  const handleResend = async () => {
    if (!canResend) return;
    await resendOtp();
    setOtpValue('');
    otpInputRef.current?.focus();
  };

  const handleBackToSignIn = () => {
    setOtpValue('');
    setError(null);
    resetOtp();
    setMode('password');
    form.clearErrors('password');
  };

  const cardDescription =
    mode === 'password'
      ? 'Enter your credentials to access your account'
      : codeSent
        ? 'Enter the OTP sent to your email'
        : 'Enter your email to receive an OTP';

  const displayError = mode === 'password' ? error : otpError;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-xl">
            TF
          </div>
          <CardTitle className="text-2xl font-bold">Welcome back</CardTitle>
          <CardDescription>{cardDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          {displayError && (
            <Alert variant="destructive" className="mb-6">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{displayError}</AlertDescription>
            </Alert>
          )}

          <Form {...form}>
            {mode === 'password' ? (
              <form onSubmit={form.handleSubmit(onPasswordSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="john@example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel>Password</FormLabel>
                        <Link
                          to="/forgot-password"
                          className="text-sm text-primary hover:underline"
                        >
                          Forgot password?
                        </Link>
                      </div>
                      <FormControl>
                        <Input type="password" placeholder="••••••••" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" className="w-full" disabled={isLoading || sending || verifying}>
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleSignInWithOtp}
                  disabled={isLoading || sending || verifying}
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Sign in with OTP
                </Button>
              </form>
            ) : !codeSent ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void handleSendOtp();
                }}
                className="space-y-4"
              >
                {/* Same form control + FormField pattern as the password email field */}
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="john@example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" className="w-full" disabled={sending}>
                  {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {sending ? 'Sending OTP...' : 'Send OTP'}
                </Button>

                <OrDivider />

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleBackToSignIn}
                  disabled={sending}
                >
                  Back to sign in with password
                </Button>
              </form>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void handleOtpSignIn();
                }}
                className="space-y-4"
              >
                <p className="text-sm text-muted-foreground text-center">
                  We have sent OTP to email id{' '}
                  <span className="font-medium text-foreground">{otpUserId}</span>.
                  It is valid for 10 minutes.
                </p>

                <div className="space-y-2">
                  <label htmlFor="otp" className="text-sm font-medium leading-none">
                    OTP
                  </label>
                  <Input
                    id="otp"
                    ref={otpInputRef}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="Enter 6-digit OTP"
                    value={otpValue}
                    onChange={(e) => handleOtpChange(e.target.value)}
                    disabled={sending || verifying}
                    className="tracking-widest text-center text-lg"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={!otpComplete || sending || verifying}
                >
                  {verifying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {verifying ? (
                    'Signing in...'
                  ) : (
                    <>
                      <LogIn className="mr-2 h-4 w-4" />
                      Sign in
                    </>
                  )}
                </Button>

                <div className="text-center">
                  {sending ? (
                    <p className="text-sm text-muted-foreground">Sending OTP...</p>
                  ) : canResend ? (
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={verifying}
                      className="text-sm font-medium text-primary hover:underline disabled:opacity-50"
                    >
                      Resend OTP
                    </button>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Resend in {resendSecondsLeft}s
                    </p>
                  )}
                </div>

                <OrDivider />

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleBackToSignIn}
                  disabled={sending || verifying}
                >
                  Back to sign in with password
                </Button>
              </form>
            )}
          </Form>
        </CardContent>
        {mode === 'password' && (
          <CardFooter className="flex justify-center">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/signup" className="text-primary hover:underline">
                Sign up
              </Link>
            </p>
          </CardFooter>
        )}
      </Card>
    </div>
  );
}
